import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { X } from 'lucide-react';
import './Sheet.css';

const focusableSelector =
  'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

const Sheet = ({ open, onClose, title, children, closeOnBackdrop = true, centered = false, noTranslate = false, fast = false, instant = false, autoFocusFirst = true, closeLabel = 'Fechar' }) => {
  const ref = useRef(null);
  const prevFocus = useRef(null);
  const focusTimer = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    prevFocus.current = typeof document !== 'undefined' ? document.activeElement : null;
    const previousOverflow = document.body.style.overflow;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
      if (e.key === 'Tab' && ref.current) {
        const nodes = ref.current.querySelectorAll(focusableSelector);
        if (!nodes.length) return;
        const first = nodes[0];
        const last = nodes[nodes.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    if (autoFocusFirst) {
      focusTimer.current = setTimeout(() => {
        const first = ref.current?.querySelector(focusableSelector);
        first?.focus();
      }, fast ? 0 : 80);
    }
    return () => {
      document.removeEventListener('keydown', onKey);
      clearTimeout(focusTimer.current);
      focusTimer.current = null;
      document.body.style.overflow = previousOverflow;
      prevFocus.current?.focus?.();
    };
  }, [open, onClose, fast, autoFocusFirst]);

  if (typeof document === 'undefined') return null;

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          className={`gm-sheet__backdrop ${centered ? 'gm-sheet__backdrop--centered' : ''}`}
          initial={instant ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: instant ? 0 : fast ? 0.08 : 0.22 }}
          onClick={() => { if (closeOnBackdrop) onClose?.(); }}
        >
          <motion.div
            ref={ref}
            className={`gm-sheet ${centered ? 'gm-sheet--centered' : ''}`}
            data-gm-no-translate={noTranslate ? 'true' : undefined}
            role="dialog"
            aria-modal="true"
            aria-label={title}
            initial={instant ? false : { opacity: 0, scale: 0.96, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={instant ? { opacity: 0 } : { opacity: 0, scale: 0.96, y: 20 }}
            transition={{ duration: instant ? 0 : fast ? 0.1 : 0.28, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="gm-sheet__handle" aria-hidden="true" />
            {title && (
              <header className="gm-sheet__header">
                <h2 className="gm-sheet__title">{title}</h2>
                <button type="button" className="gm-sheet__close" aria-label={closeLabel} onClick={onClose}>
                  <X size={18} strokeWidth={1.75} />
                </button>
              </header>
            )}
            <div className="gm-sheet__body">{children}</div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
};

Sheet.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  title: PropTypes.string,
  children: PropTypes.node,
  closeOnBackdrop: PropTypes.bool,
  centered: PropTypes.bool,
  noTranslate: PropTypes.bool,
  fast: PropTypes.bool,
  instant: PropTypes.bool,
  autoFocusFirst: PropTypes.bool,
  closeLabel: PropTypes.string,
};

export default Sheet;
